import React, { useEffect, useState } from 'react';
import { Button, Loading } from 'react-style-guide';
import { useTranslation } from 'react-utilities';
import { AccessManagementUpsellV2Service, EmailVerificationService, EnvironmentUrls } from 'Roblox';
import sendNotApprovedPageEvent from '../services/sendNotApprovedPageEvent';
import { EventTypes } from '../services/eventConstants';
import universalAppConfigService from '../services/universalAppConfigService';
import reactivateAccount from '../utils/reactivateAccount';
import { AMPRecoursePunishmentType, ProceedAction } from '../utils/types';
import { ACCESS_MANAGEMENT_UPSELL_CAN_LIFT_PUNISHMENT_FEATURE_NAME } from '../utils/constants';

const { websiteUrl } = EnvironmentUrls;

const parentVerificationUrl = `${websiteUrl}/parents/account-setup`;

type Props = {
  proceedAction: ProceedAction;
  isDisabled?: boolean;
};

const NotApprovedProceedCTAButton = ({ proceedAction, isDisabled }: Props): JSX.Element => {
  const { translate } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isDecomposedVPCEnabled, setIsDecomposedVPCEnabled] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    setIsLoading(true);
    // eslint-disable-next-line no-void
    void (async () => {
      const decomposedVPCEnabled = await universalAppConfigService.getVPCStatus();
      setIsDecomposedVPCEnabled(decomposedVPCEnabled);
      setIsLoading(false);
    })();
  }, []);

  let buttonText: string;
  switch (proceedAction) {
    case ProceedAction.Reactivate:
      buttonText = translate('Action.Reactivate');
      break;
    case ProceedAction.VerifyEmail:
      buttonText = translate('Label.EmailVerificationButtonText');
      break;
    case ProceedAction.VerifyVPC:
      buttonText = translate('Label.ParentVerificationButtonText');
      break;
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      console.warn(`proceedAction has impossible value ${proceedAction}`);
      buttonText = '';
      break;
  }

  const handleOnClick = () => {
    switch (proceedAction) {
      case ProceedAction.Reactivate:
        sendNotApprovedPageEvent(EventTypes.ReactivateClicked);
        // eslint-disable-next-line no-void
        void reactivateAccount(setIsLoading);
        break;
      case ProceedAction.VerifyEmail:
        sendNotApprovedPageEvent(EventTypes.EmailVerificationClicked);
        // eslint-disable-next-line no-void
        void EmailVerificationService.handleUserEmailVerificationRequiredByBan();
        break;
      case ProceedAction.VerifyVPC:
        sendNotApprovedPageEvent(EventTypes.ParentVerificationClicked);
        if (isDecomposedVPCEnabled) {
          // eslint-disable-next-line no-void
          void AccessManagementUpsellV2Service.startAccessManagementUpsell({
            featureName: ACCESS_MANAGEMENT_UPSELL_CAN_LIFT_PUNISHMENT_FEATURE_NAME,
            ampRecourseData: { punishmentType: AMPRecoursePunishmentType.Chargeback },
            isAsyncCall: true,
            usePrologue: false
          });
        } else {
          window.location.href = parentVerificationUrl;
        }
        break;
      default:
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.warn(`proceedAction has impossible value ${proceedAction}`);
        break;
    }
  };

  return (
    <Button
      // TODO: Solution to keep button width is a bit hacky. Revisit in future
      className={`enable-three-dee btn-control ${isLoading ? 'transparent' : ''}`}
      size={Button.sizes.medium}
      variant={Button.variants.primary}
      width={Button.widths.default}
      onClick={handleOnClick}
      isDisabled={isDisabled}>
      {isLoading && <Loading className='spinner-xs spinner-no-margin' />}
      {buttonText}
    </Button>
  );
};

NotApprovedProceedCTAButton.defaultProps = {
  isDisabled: false
};

export default NotApprovedProceedCTAButton;
